import React, { useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, TextField } from "@mui/material";
import { colors } from "theme/defaultStyle";
import { InputBaseComponentProps } from "@mui/material/InputBase/InputBase";

interface Props<Criteria extends Record<string, any>> {
  name: keyof Criteria & string;
  onChange: (name: keyof Criteria & string, value: string | null) => void;
  defaultValue?: string;
  disabled?: boolean;
  inputComponent?: React.ElementType<InputBaseComponentProps>;
  inputProps?: InputBaseComponentProps;
}

const InputSearch = <Criteria extends Record<string, any>>({
  name,
  onChange,
  defaultValue,
  disabled,
  inputComponent,
  inputProps = {},
}: Props<Criteria>) => {
  const [value, setValue] = useState<string>(defaultValue || "");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputName = event.target.name as keyof Criteria & string;
    const currentValue = event.target.value; // .trim() ? event.target.value.trim() : null;
    if (value !== event.target.value.trim()) {
      onChange(inputName, currentValue);
    }
    setValue(currentValue || "");
  };

  const clearInput = () => {
    setValue("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
      const event = new Event("input", { bubbles: true });
      Object.defineProperty(event, "target", {
        value: searchInputRef.current,
        enumerable: true,
      });
      searchInputRef.current.dispatchEvent(event);
      const changeEvent = {
        target: searchInputRef.current,
      } as React.ChangeEvent<HTMLInputElement>;
      handleInputChange(changeEvent);
    }
  };

  return (
    <TextField
      {...{ name }}
      id={name}
      onChange={handleInputChange}
      disabled={disabled}
      placeholder="Search"
      inputRef={searchInputRef}
      inputProps={inputProps}
      sx={{
        width: "100%",
      }}
      InputProps={{
        inputComponent: inputComponent,
        endAdornment: !!value && (
          <IconButton
            onClick={clearInput}
            disabled={disabled}
            sx={{ color: colors.blue14 }}
          >
            <CloseIcon />
          </IconButton>
        ),
      }}
      InputLabelProps={{
        shrink: false,
      }}
      value={value}
    />
  );
};

export default InputSearch;
